import React, {useRef, useState,useEffect} from 'react'
import Container from 'components/Container'
import { Col, Row, Button, Dropdown, Menu, Typography, Card} from 'antd'
import { BASE_GUTTER, MODAL_SIZE_EXTRA_LARGE } from 'constants/Layout';
import AuthorisedContainer from 'components/AuthorisedContainer';
import { PermissionSetMap } from 'constants/RBACConstants';
import Route53Listing from './Route53Listing';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import useEnvironmentVariables from "hooks/useEnvironmentVariables"
import ModalUtil from 'utils/modalUtil';
import { scriptDownloadUrl, tokenGenerateSection } from '../aws/AWSWebhookSuccessPopup';
import BaseModal from 'components/BaseModal';
import AWSIntegrationListing from '../aws-integration/AWSIntegrationListing';
import { useFeatureFlag } from 'contexts/FeatureFlagContext';
import AWSIntegrationModalForm from '../aws-integration/AWSIntegrationModalForm';
const { Title, Paragraph, Text, Link } = Typography;

const TerraformScript = ({data}) => <><Title level={4}>Setup using Terraform Script</Title>
<Paragraph><Text strong>Step 1</Text> Download the zip file <Link href={scriptDownloadUrl} target='_blank'>(Armorcode-AWS-TF-Scripts.zip)</Link>, unzip and save it to a folder. The main folder will contain individual folders corresponding to the different AWS security tools.</Paragraph>
<Paragraph><Text strong>Step 2</Text> Open the terminal/command prompt and navigate to the folder of <Text strong>webhook-aws-infradiscovery</Text>.</Paragraph>
<Paragraph><Text strong>Step 3</Text> Initialize Terraform by running the command <Text code>terraform init</Text></Paragraph>
<Paragraph><Text strong>Step 4</Text> Once the terminal displays that Terraform has been successfully initialized, run the command <Text code>terraform apply</Text></Paragraph>
<Paragraph><Text strong>Step 5</Text> After running the above command, the user will be prompted to enter the below details.</Paragraph>
<ul>
    <li><Text strong>Account ID</Text> - <>Enter the Account ID associated with your ArmorCode instance. Press Enter to accept the default value: 245607616603.</></li>
    <li><Text strong>Armorcode Domain</Text> -  Please provide the Domain URL associated with your ArmorCode instance. Press Enter to accept the default value: <Link href={"https://app.armorcode.com/"} target='_blank'>https://app.armorcode.com</Link>.</li>
    <li><Text strong>External ID</Text> - <>{tokenGenerateSection(data.externalId)}</></li>
    <li><Text strong>Region</Text> - <>Provide the region where AWS Infra is enabled in your AWS account.</></li>
    <li><Text strong>Request ID</Text> - <>{tokenGenerateSection(data.requestId)}</></li>
</ul>
<Paragraph><Text strong>Step 6</Text> After the data has been processed, the user will be asked to confirm if the actions need to be performed. Input the answer as <Text strong>yes</Text></Paragraph>
<Paragraph><Text strong>Step 7</Text> If all the inputs were valid and if the webhook was configured successfully, an <Text strong>"Apply Complete"</Text> message will appear along with the outputs.</Paragraph>
<Paragraph><Text strong>Step 8</Text> After successfully executing Terraform, you should either create a support ticket with ArmorCode or send an email to ArmorCode Support and share AWS Infra terraform execution output along with the userId, account and Org details. Within 24 hours of your submission, our team will review the outputs, validate them, and make the necessary updates in the backend.</Paragraph>
<Paragraph><Text strong>Step 9</Text> After the entry is created, navigate to Discovery {">"} AWS Infra {">"} Click on the Sync button. After a few minutes, refresh the page and the AWS infrastructure assets will be listed.</Paragraph></>

function Route53Dashboard({ isAWSInfra, ...props }) {
  const [intervalId, setIntervalId] = useState();
  const [loading, setLoading] = useState(false)
  const [awsIntegrationModalVisible, setAwsIntegrationModalVisible] = useState(false);
  const installtionRef = useRef();
  const configurationRef = useRef();
  const APP_AWS_YML_FILE_NAME = useEnvironmentVariables('REACT_APP_AWS_YML_FILE_NAME');
  const ROUTE53_YML_FILE_NAME = useEnvironmentVariables('REACT_APP_ROUTE53_YML_FILE_NAME');
  const featureFlags = useFeatureFlag();

  useEffect(() => {
    return () => clearInterval(intervalId);
  }, [intervalId]);

  const openRoute53IntegrationPage = (data) => {
    setLoading(false);
    const stackName = isAWSInfra ? "ArmorAWSInfraStack" : "ArmorCodeDiscovery";
    const stackUrl = isAWSInfra ? APP_AWS_YML_FILE_NAME : ROUTE53_YML_FILE_NAME;
    let url = `https://us-west-2.console.aws.amazon.com/cloudformation/home?region=us-west-2#/stacks/quickcreate?stackName=${stackName}&templateURL=https://armorcode.s3.us-west-2.amazonaws.com/${stackUrl}&param_ExternalId=${data.externalId}&param_RequestId=${data.requestId}`
    const _window = window.open(url, "_blank", "width=1000,height=800,rel=noopener");
    const timer = setInterval(function() { 
        if(_window.closed) {
            afterPopupClose()
            clearInterval(timer);
        }
    }, 1000);
    setIntervalId(timer);
  }

  const connectToRoute53 = (key) => {
    setLoading(true)
    if (key === "addAWSAccounts") {
      setAwsIntegrationModalVisible(true);
      setLoading(false);
      return;
    }
    ToolsIntegrationsAPI[isAWSInfra ? "getAWSIntegrate" : "getRoute53Integrate"]().promise
      .then(res => {
        if(key === "cloudFormation"){
          openRoute53IntegrationPage(res);
        }else if(key === "terraform"){
          ModalUtil.show({
            content: (<BaseModal modalProps={{ footer: null, width: MODAL_SIZE_EXTRA_LARGE }} afterClose={afterPopupClose}><Card bordered={false}>{<TerraformScript data={res}/>}</Card></BaseModal>),
          });
          setLoading(false);
        }
      })
      .catch(err => {
        console.error(err)
        setLoading(false)
      })
  }

  const afterPopupClose = () => {
    if(installtionRef && installtionRef.current && installtionRef.current.refresh) {
      installtionRef.current.refresh()
    }
  }

  const afterCreateAccountPopupClose = () => {
    setAwsIntegrationModalVisible(false);
    if(configurationRef.current?.refresh) {
      configurationRef.current.refresh()
    }
  }

  const handleMenuClick = ({key}) => {
      connectToRoute53(key);
  }

  const menu =  (
    <Menu onClick={handleMenuClick}>
      {featureFlags.RBVM_ASSETS && <Menu.Item key="addAWSAccounts">
        Add AWS Accounts
      </Menu.Item>
      }
      <Menu.Item key="cloudFormation">
        {isAWSInfra ? "Add AWS Account using CloudFormation" : "Connect to Route 53"}
      </Menu.Item>
      <Menu.Item key="terraform">
        Add AWS Account using Terraform
      </Menu.Item>
    </Menu>
  );
  
  return (
      <>
      {awsIntegrationModalVisible && (
        <AWSIntegrationModalForm
          visible={awsIntegrationModalVisible}
          afterClose={afterCreateAccountPopupClose}
        />
      )}
      <Row gutter={BASE_GUTTER}>
        <Col span={24} className="d-flex justify-content-end align-items-center">
          <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
            <Dropdown overlay={menu} trigger={["click"]}>
              <Button loading={loading} type='primary' size="small">
                Add
              </Button>
            </Dropdown>
          </AuthorisedContainer>
        </Col>
      </Row>
        <Container className="m-b-sm" bodyStyle={{paddingTop: "15px"}}>
            <Route53Listing isAWSInfra={isAWSInfra} installtionRef={installtionRef} />
        </Container>
        {isAWSInfra && featureFlags.RBVM_ASSETS && <Container className="m-b-sm" bodyStyle={{paddingTop: "15px"}}>
          <AWSIntegrationListing {...props} configurationRef={configurationRef}/>
        </Container>}
      </>
  )
}

export default Route53Dashboard
